import {
  Circle,
  CheckCircle2,
  Loader,
  XCircle,
  Archive,
  FileCheck,
  Tag,
} from "lucide-react";
import type { FilterCategoryGroup } from "@/components/ui";
import type { AuditTag } from "@/packages/types";
import { getAuditStatusConfig } from "./constants";

/**
 * Builds the base audit filter categories with palette-derived status colors.
 * Evaluated lazily (at call time) so CSS variables are available.
 */
function buildBaseFilterCategories(): FilterCategoryGroup[] {
  return [
    {
      label: "Audit Properties",
      categories: [
        {
          id: "status",
          label: "Status",
          icon: Circle,
          allowedOperators: ["is", "is_not", "is_either_of"],
          values: [
            {
              id: "pending",
              label: "Pending",
              icon: Circle,
              color: getAuditStatusConfig("pending").dotColor,
            },
            {
              id: "in_progress",
              label: "In Progress",
              icon: Loader,
              color: getAuditStatusConfig("in_progress").dotColor,
            },
            {
              id: "submitted",
              label: "Submitted",
              icon: FileCheck,
              color: getAuditStatusConfig("submitted").dotColor,
            },
            {
              id: "approved",
              label: "Approved",
              icon: CheckCircle2,
              color: getAuditStatusConfig("approved").dotColor,
            },
            {
              id: "rejected",
              label: "Rejected",
              icon: XCircle,
              color: getAuditStatusConfig("rejected").dotColor,
            },
            {
              id: "archived",
              label: "Archived",
              icon: Archive,
              color: getAuditStatusConfig("archived").dotColor,
            },
          ],
        },
        {
          id: "trigger",
          label: "Trigger",
          icon: Tag,
          allowedOperators: ["is", "is_not", "is_either_of"],
          values: [],
        },
        {
          id: "tags",
          label: "Tags",
          icon: Tag,
          allowedOperators: ["includes_any", "includes_all", "includes_none"],
          values: [],
        },
      ],
    },
  ];
}

/**
 * @deprecated Use getAuditFilterCategories() instead for palette-aware colors.
 * Kept for backward compatibility — builds fresh on each access.
 */
export const AUDIT_FILTER_CATEGORIES: FilterCategoryGroup[] = new Proxy(
  [] as FilterCategoryGroup[],
  {
    get(_target, prop) {
      const categories = buildBaseFilterCategories();
      return Reflect.get(categories, prop);
    },
  },
);

export function getAuditFilterCategories(
  tags: AuditTag[],
  triggers: string[],
): FilterCategoryGroup[] {
  return buildBaseFilterCategories().map((group) => {
    if (group.categories) {
      return {
        ...group,
        categories: group.categories.map((category) => {
          if (category.id === "tags") {
            return {
              ...category,
              values: tags.map((tag) => ({
                id: tag.id,
                label: tag.name,
                color: tag.color,
              })),
            };
          }
          if (category.id === "trigger") {
            return {
              ...category,
              values: triggers.map((t) => ({ id: t, label: t })),
            };
          }
          return category;
        }),
      };
    }
    return group;
  });
}
